import {
  Button,
  Col,
  Divider,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Switch,
  Tag,
  Tooltip,
  message
} from 'antd';
import { useForm } from 'antd/lib/form/Form';
import { InfoCircleFilled, SaveFilled } from '@ant-design/icons';
import React, { useCallback, useEffect, useState } from 'react';
import { useApi } from '../../hooks/api';
import './styles.scss';

const { Option } = Select;

interface IProps {
  licenseId: string;
  clientId: string;
  saveLicense(data: any): Promise<void>;
  setShowLicenseForm: (value: boolean) => void;
  onClickExtraPackage: () => void;
}

const LicenseForm: React.FC<IProps> = props => {
  const {
    saveLicense,
    licenseId,
    clientId,
    setShowLicenseForm,
    onClickExtraPackage,
  } = props;
  const { api } = useApi();
  const [loading, setLoading] = useState(false);
  // const [dividirCategorias, setDividirCategorias] = useState(true);
  const [form] = useForm();
  const [formLicenseId, setFormLicenseId] = useState('');
  const [licenseDisabled, setLicenseDisabled] = useState(false);
  const [extraPackagesTotal, setExtraPackagesTotal] = useState(0);

  const formatCurrency = (value: any) => {
    if (value === undefined || value === null || value === '') return '';
    const num = Number(String(value).replace(',', '.'));
    // eslint-disable-next-line no-restricted-globals
    if (isNaN(num)) return value;
    return num.toFixed(2).replace('.', ',');
  };

  const parseCurrency = (value: string) => {
    if (!value) return 0;
    return Number(value.replace(/\./g, '').replace(',', '.'));
  };

  const sanitizeCurrencyInput = (value: string) => {
    const sanitized = value.replace(/R\$\s?/g, '').replace(/[^\d,]/g, '');
    const [intPart = '', ...decimalParts] = sanitized.split(',');
    return decimalParts.length
      ? `${intPart},${decimalParts.join('')}`
      : intPart;
  };

  const getSaldoColor = (saldo: number, limite: number) => {
    if (!limite || limite <= 0) {
      return saldo < 0 ? '#ff4d4f' : '#52c41a';
    }
    const percentual = (saldo / limite) * 100;
    if (percentual < 10) return '#ff4d4f';
    if (percentual < 30) return '#fa8c16';
    if (percentual < 50) return '#fadb14';
    return '#52c41a';
  };

  const values = Form.useWatch([], form) || {};
  const mediaName = Form.useWatch('media', form);
  const accountType = Form.useWatch('account_type', form) || 'PREPAID';
  const packageTotal = parseCurrency(values.package_total_value) || 0;
  const packageTotalWithExtras = packageTotal + extraPackagesTotal;
  const packageTotalValue = Form.useWatch('package_total_value', form);
  const dividirCategorias = Form.useWatch('dividir_categorias', form);
  const previousDividirCategorias = React.useRef<boolean | undefined>(undefined);

  const [spendMkt, setSpendMkt] = React.useState(0);
  const [spendAuth, setSpendAuth] = React.useState(0);
  const [spendUtl, setSpendUtl] = React.useState(0);
  const [spendSvc, setSpendSvc] = React.useState(0);
  const [spendPackage, setSpendPackage] = React.useState(0);

  const [isTransferModalOpen, setIsTransferModalOpen] = React.useState(false);
  const [transferFrom, setTransferFrom] = React.useState<string>(''); 
  const [transferTo, setTransferTo] = React.useState<string>('');
  const [transferValue, setTransferValue] = React.useState<string>('');

  const categoryLabels: Record<string, string> = {
    mkt: 'Marketing',
    utl: 'Utilidades',
    auth: 'Autenticação',
    svc: 'Serviço',
  };

  const parseTransferValue = (value: string) => {
    if (!value) return 0;
    const sanitized = value
      .replace(/R\$\s?/g, '')
      .replace(/[^\d,]/g, '');
    const [intPart = '', ...decimalParts] = sanitized.split(',');
    const normalized = decimalParts.length
      ? `${intPart},${decimalParts.join('')}`
      : intPart;
    return parseCurrency(normalized) || 0;
  };

  const spendByCategory: Record<string, number> = {
    mkt: spendMkt,
    auth: spendAuth,
    utl: spendUtl,
    svc: spendSvc,
  };

  const currentLimitFrom =
    parseCurrency(form.getFieldValue(`package_${transferFrom}_value`)) || 0;
  const spendFrom = spendByCategory[transferFrom] || 0;
  const saldoRestanteFrom = currentLimitFrom - spendFrom;
  const transferNumericValue = parseTransferValue(transferValue);
  const transferExceedsLimit = transferNumericValue > saldoRestanteFrom;

  const mktLimit = parseCurrency(values.package_mkt_value) || 0;
  const authLimit = parseCurrency(values.package_auth_value) || 0;
  const utlLimit = parseCurrency(values.package_utl_value) || 0;
  const svcLimit = parseCurrency(values.package_svc_value) || 0;

  useEffect(() => {
    if (values.media !== 'whatsapp') return;

    if (dividirCategorias) {
      // Cenário COM divisão por categorias
      const totalLimitesCategorias =
        mktLimit + authLimit + utlLimit + svcLimit;

      // Calcula os saldos restantes individuais (podem ficar negativos)
      const saldoMkt = mktLimit - spendMkt;
      const saldoAuth = authLimit - spendAuth;
      const saldoUtl = utlLimit - spendUtl;
      const saldoSvc = svcLimit - spendSvc;
      const totalSaldoRestante = saldoMkt + saldoAuth + saldoUtl + saldoSvc;

      form.setFieldsValue({
        remaining_balance_mkt: formatCurrency(saldoMkt),
        remaining_balance_auth: formatCurrency(saldoAuth),
        remaining_balance_utl: formatCurrency(saldoUtl),
        remaining_balance_svc: formatCurrency(saldoSvc),
        total_categories_limit: formatCurrency(totalLimitesCategorias),
        total_remaining_balance: formatCurrency(totalSaldoRestante)
      });

    } else {
      // Cenário SEM divisão (Simplificado) - saldo pode ficar negativo
      const saldoSimplificado = packageTotalWithExtras - spendPackage;

      form.setFieldsValue({
        package_limit_total_display: formatCurrency(packageTotalWithExtras),
        remaining_balance: formatCurrency(saldoSimplificado)
      });
    }
  }, [
    values.package_mkt_value, 
    values.package_auth_value, 
    values.package_utl_value, 
    values.package_svc_value,
    packageTotal,
    packageTotalWithExtras,
    dividirCategorias, 
    spendMkt, spendAuth, spendUtl, spendSvc, spendPackage, 
    form
  ]);

  const totalLimites = mktLimit + authLimit + utlLimit + svcLimit;

  // Arredonda para centavos antes de comparar, evitando falsos positivos
  // causados por imprecisão de ponto flutuante (ex: 33.33 + 33.33 + 33.34 !== 100)
  const totalLimitesArredondado = Math.round(totalLimites * 100) / 100;
  const packageTotalWithExtrasArredondado =
    Math.round(packageTotalWithExtras * 100) / 100;

  const excedeuLimite =
    dividirCategorias &&
    totalLimitesArredondado > packageTotalWithExtrasArredondado;
  const ficouAbaixoLimite =
    dividirCategorias &&
    totalLimitesArredondado < packageTotalWithExtrasArredondado;

  const possuiErroDeLimite = excedeuLimite || ficouAbaixoLimite;
  const valorDiferenca = Math.abs(
    totalLimitesArredondado - packageTotalWithExtrasArredondado,
  );

  const cancelLicenseEdit = () => {
    form.resetFields();
    setShowLicenseForm(false);
  };

  const loadOneLicense = useCallback(async (id: string) => {
    const response = await api.get(`/licenses/${id}`);
    setFormLicenseId(response.data.id);
    const currentMedia = String(response.data.media || '').toLowerCase();
    const currentAccountType = response.data.account_type || 'PREPAID';
    setLicenseDisabled(response.data.enabled === false);

    const whatsappDetails = response.data.whatsapp_details;
    const whatsappDetail = Array.isArray(whatsappDetails)
      ? whatsappDetails[0]
      : response.data.whatsapp_detail || whatsappDetails;
    
    if (currentMedia === 'whatsapp' && whatsappDetail) {
      const parseApiMoney = (value: any) => {
        if (value === undefined || value === null || value === '') return 0;
        const normalized = String(value).replace(',', '.');
        const parsed = Number(normalized);
        // eslint-disable-next-line no-restricted-globals
        return isNaN(parsed) ? 0 : parsed;
      };

      const extraPackagesArrayTotal = Array.isArray(response.data.extra_packages)
        ? response.data.extra_packages.reduce(
          (total: number, item: any) => total + parseApiMoney(item?.package_value),
          0,
        )
        : 0;

      const extraPackagesTotalValue =
        Number(
          response.data.extra_packages_total_value
          ?? response.data.extra_package_total_value
          ?? whatsappDetail.extra_packages_total_value
          ?? whatsappDetail.extra_package_total_value,
        )
        || extraPackagesArrayTotal
        || 0;

      const packageTotalValue = Number(whatsappDetail.package_total_value) || 0;
      const basePackageTotalValue = Math.max(
        0,
        packageTotalValue - extraPackagesTotalValue,
      );

      setExtraPackagesTotal(extraPackagesTotalValue);

      const temDivisao = whatsappDetail.unit_value === null || whatsappDetail.unit_value === undefined;

      setSpendMkt(Number(whatsappDetail.spend_mkt_value) || 0);
      setSpendAuth(Number(whatsappDetail.spend_auth_value) || 0);
      setSpendUtl(Number(whatsappDetail.spend_utl_value) || 0);
      setSpendSvc(Number(whatsappDetail.spend_svc_value) || 0);
      setSpendPackage(Number(whatsappDetail.spend_package_value) || 0);

      form.setFieldsValue({
        media: currentMedia,
        account_type: currentAccountType,
        package_total_value: formatCurrency(basePackageTotalValue),
        dividir_categorias: temDivisao,
        package_extra_total_display: formatCurrency(extraPackagesTotalValue),

        package_limit_total_display: formatCurrency(packageTotalWithExtras),
        unit_value: formatCurrency(whatsappDetail.unit_value),
        
        // Marketing
        package_mkt_value: formatCurrency(whatsappDetail.package_mkt_value),
        unit_mkt_value: formatCurrency(whatsappDetail.unit_mkt_value),
        
        // Autenticação
        package_auth_value: formatCurrency(whatsappDetail.package_auth_value),
        unit_auth_value: formatCurrency(whatsappDetail.unit_auth_value),
        
        // Utilidades
        package_utl_value: formatCurrency(whatsappDetail.package_utl_value),
        unit_utl_value: formatCurrency(whatsappDetail.unit_utl_value),

        // Serviço
        package_svc_value: formatCurrency(whatsappDetail.package_svc_value),
        unit_svc_value: formatCurrency(whatsappDetail.unit_svc_value),
        
      });
    } else {
      setExtraPackagesTotal(0);
      // Para SMS ou caso não tenha detalhes ainda
      form.setFieldsValue({
        ...response.data,
        media: currentMedia,
        account_type: currentAccountType,
        available_quantity: response.data.available_quantity,
        package_value: formatCurrency(response.data.package_value),
        extra_message_value: formatCurrency(response.data.extra_message_value),
      });
    }
  }, [api, form]);

  useEffect(() => {
    if (licenseId) {
      loadOneLicense(licenseId);
    }
  }, [licenseId, loadOneLicense]);

  useEffect(() => {
    if (!licenseId && mediaName === 'whatsapp' && accountType === 'PREPAID') {
      form.setFieldsValue({
        dividir_categorias: false,
      });
    }
  }, [licenseId, mediaName, accountType, form]);

  useEffect(() => {
    if (dividirCategorias === false) {
      form.setFieldsValue({
        package_limit_total_display: formatCurrency(packageTotalWithExtras),
      });
    }
  }, [packageTotalWithExtras, dividirCategorias, form]);

  useEffect(() => {
    if (typeof dividirCategorias !== 'boolean') return;

    const houveTrocaRealDoSwitch =
      previousDividirCategorias.current !== undefined
      && previousDividirCategorias.current !== dividirCategorias;

    if (houveTrocaRealDoSwitch) {
      if (!dividirCategorias) {
        form.setFieldsValue({
          package_mkt_value: undefined,
          unit_mkt_value: undefined,
          package_auth_value: undefined,
          unit_auth_value: undefined,
          package_utl_value: undefined,
          unit_utl_value: undefined,
          package_svc_value: undefined,
          unit_svc_value: undefined,
        });
      } else {
        form.setFieldsValue({
          unit_value: undefined,
        });
      }
    }

    previousDividirCategorias.current = dividirCategorias;
  }, [dividirCategorias, form]);

  const onSave = useCallback(async (values: any) => {
    try {
      const isPrepaid = values.account_type === 'PREPAID';

      setLoading(true);

      const dividirCategorias = values.dividir_categorias;

      const payload = {
        client_id: clientId,
        id: formLicenseId || undefined,
        media: values.media,
        account_type: values.account_type,
        ...(values.media === 'whatsapp' && isPrepaid && {
          whatsapp_detail: dividirCategorias ? {
            package_total_value: parseCurrency(values.package_total_value),
            package_mkt_value: parseCurrency(values.package_mkt_value),
            unit_mkt_value: parseCurrency(values.unit_mkt_value),
            package_auth_value: parseCurrency(values.package_auth_value),
            unit_auth_value: parseCurrency(values.unit_auth_value),
            package_utl_value: parseCurrency(values.package_utl_value),
            unit_utl_value: parseCurrency(values.unit_utl_value),
            package_svc_value: parseCurrency(values.package_svc_value),
            unit_svc_value: parseCurrency(values.unit_svc_value),
          } : {
            package_total_value: parseCurrency(values.package_total_value),
            unit_value: parseCurrency(values.unit_value),
          }
        }),
        ...(values.media === 'sms' && isPrepaid && {
          available_quantity: values.available_quantity,
          package_value: parseCurrency(values.package_value),
          extra_message_value: parseCurrency(values.extra_message_value)
        })
      };

      await saveLicense(payload);
      setLoading(false);
      setShowLicenseForm(false);
    } catch (err) {
      setLoading(false);
    }
  }, [clientId, formLicenseId, saveLicense, setShowLicenseForm]);

  const handleOpenTransfer = (categoryKey: string) => {
    setTransferFrom(categoryKey);
      
    const disponiveis = ['mkt', 'utl', 'auth', 'svc'].filter(cat => cat !== categoryKey);
    setTransferTo(disponiveis[0]);
    setTransferValue('');
    setIsTransferModalOpen(true);
  };

  const handleExecuteTransfer = () => {
    const currentLimitFrom = parseCurrency(form.getFieldValue(`package_${transferFrom}_value`)) || 0;
    const currentLimitTo = parseCurrency(form.getFieldValue(`package_${transferTo}_value`)) || 0;
    const transferAmount = parseTransferValue(transferValue);
    const spendFrom = spendByCategory[transferFrom] || 0;
    const saldoRestanteFrom = currentLimitFrom - spendFrom;

    if (transferAmount > saldoRestanteFrom) {
      message.error('O valor digitado excede o saldo restante disponível na categoria de origem.');
      return;
    }

    if (transferAmount <= 0) {
      message.error('Informe um valor válido para transferir.');
      return;
    }

    form.setFieldsValue({
      [`package_${transferFrom}_value`]: formatCurrency(currentLimitFrom - transferAmount),
      [`package_${transferTo}_value`]: formatCurrency(currentLimitTo + transferAmount),
    });

    setIsTransferModalOpen(false);
    message.success('Saldo transferido com sucesso!');
  };

  return (
    <Form
      layout="vertical"
      form={form}
      onFinish={onSave}
      className="license-form"
      initialValues={{ account_type: 'PREPAID', dividir_categorias: true }}
    >
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 18, color: '#283593' }}>⇆</span>
            <strong>Transferir saldo</strong>
          </div>
        }
        open={isTransferModalOpen}
        onCancel={() => setIsTransferModalOpen(false)}
        footer={[
          <Button
            key="cancel"
            onClick={() => setIsTransferModalOpen(false)}
            style={{ borderRadius: 16 }}
          >
            Cancelar
          </Button>,
          <Button
            key="submit"
            type="primary"
            onClick={handleExecuteTransfer}
            style={{
              backgroundColor: '#1d39c4',
              borderColor: '#1d39c4',
              borderRadius: 16,
              color: '#FFFFFF',
            }}
            disabled={transferNumericValue <= 0 || transferExceedsLimit}
          >
            <span style={{ color: '#FFFFFF' }}>⇆</span> Transferir saldo
          </Button>,
        ]}
        width={600}
      >
        <div style={{ padding: '8px 0' }}>
          <p style={{ marginBottom: 8, color: '#595959' }}>
            Para qual categoria você deseja transferir o saldo?
          </p>
          <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
            {['mkt', 'utl', 'auth', 'svc']
              .filter(cat => cat !== transferFrom)
              .map(cat => (
                <Button
                  key={cat}
                  style={{
                    flex: 1,
                    borderRadius: 16,
                    borderColor: transferTo === cat ? '#1d39c4' : '#d9d9d9',
                    color: transferTo === cat ? '#1d39c4' : '#595959',
                    fontWeight: transferTo === cat ? 'bold' : 'normal',
                  }}
                  onClick={() => setTransferTo(cat)}
                >
                  {categoryLabels[cat]}
                </Button>
              ))}
          </div>

          <p style={{ marginBottom: 8, color: '#595959' }}>
            De qual categoria você quer transferir o saldo?
          </p>
          <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
            <Button
              style={{
                flex: 1,
                borderRadius: 16,
                borderColor: '#1d39c4',
                color: '#1d39c4',
                fontWeight: 'bold',
              }}
              disabled
            >
              {categoryLabels[transferFrom]}
            </Button>
            {['mkt', 'utl', 'auth', 'svc']
              .filter(cat => cat !== transferFrom)
              .map(cat => (
                <Button
                  key={cat}
                  style={{ flex: 1, borderRadius: 16 }}
                  disabled
                >
                  {categoryLabels[cat]}
                </Button>
              ))}
          </div>

          <p style={{ marginBottom: 8, color: '#595959' }}>
            Qual valor você quer transferir?
          </p>
          <Row gutter={16} align="middle">
            <Col span={11}>
              <div
                style={{
                  border: '1px solid #f0f0f0',
                  padding: '12px',
                  borderRadius: 8,
                }}
              >
                <div
                  style={{
                    fontSize: 12,
                    color: '#1d39c4',
                    fontWeight: 'bold',
                    marginBottom: 4,
                  }}
                >
                  {categoryLabels[transferFrom]}
                </div>
                <div
                  style={{ fontSize: 20, color: '#ff4d4f', fontWeight: 'bold' }}
                >
                  R${' '}
                  {formatCurrency(saldoRestanteFrom - transferNumericValue)}
                </div>
              </div>
            </Col>

            <Col
              span={2}
              style={{ textAlign: 'center', fontSize: 20, color: '#283593' }}
            >
              ⇆
            </Col>

            <Col span={11}>
              <div
                style={{
                  border: '1px solid #f0f0f0',
                  padding: '6px 12px',
                  borderRadius: 8,
                }}
              >
                <div
                  style={{
                    fontSize: 12,
                    color: '#14A704',
                    fontWeight: 'bold',
                    marginBottom: 2,
                  }}
                >
                  {categoryLabels[transferTo]}
                </div>
                <Input
                  bordered={false}
                  style={{
                    width: '100%',
                    fontSize: 20,
                    color: '#14A704',
                    fontWeight: 'bold',
                    padding: 0,
                  }}
                  placeholder="R$ 0,00"
                  value={transferValue}
                  onChange={e => {
                    setTransferValue(sanitizeCurrencyInput(e.target.value));
                  }}
                  onBlur={e => setTransferValue(formatCurrency(e.target.value))}
                />
              </div>
              {transferExceedsLimit && (
                <div style={{ marginTop: 8, color: '#ff4d4f', fontSize: 12 }}>
                  O valor não pode ser maior que o saldo restante em {categoryLabels[transferFrom]}.
                </div>
              )}
            </Col>
          </Row>
        </div>
      </Modal>
      
      <Row gutter={16}>
        <Col
          span={
            mediaName === 'whatsapp' && accountType === 'PREPAID' && extraPackagesTotal > 0
              ? 6
              : 8
          }
        >
          <Form.Item
            label="Canal:"
            name="media"
            rules={[{ required: true, message: 'Campo obrigatório' }]}
          >
            <Select
              style={{ borderRadius: 16 }}
              disabled={!!licenseId}
              placeholder="Selecione o canal"
            >
              <Option value="whatsapp">WhatsApp</Option>
              <Option value="sms">SMS</Option>
            </Select>
          </Form.Item>
        </Col>
        <Col
          span={
            mediaName === 'whatsapp' && accountType === 'PREPAID' && extraPackagesTotal > 0
              ? 6
              : 8
          }
        >
          <Form.Item
            label="Tipo de conta:"
            name="account_type"
            rules={[{ required: true, message: 'Campo obrigatório' }]}
          >
            <Select
              style={{ borderRadius: 16 }}
              placeholder="Tipo de conta"
            >
              <Option value="PREPAID">Pré-pago</Option>
              <Option value="POSTPAID">Pós-pago</Option>
            </Select>
          </Form.Item>
        </Col>
        <Col
          span={
            mediaName === 'whatsapp' && accountType === 'PREPAID' && extraPackagesTotal > 0
              ? 6
              : 8
          }
        >
          <Form.Item
            label="Valor total do pacote"
            name="package_total_value"
            rules={[
              { required: true, message: 'Campo obrigatório' },
              {
                validator: (_, value) => {
                  if (mediaName === 'whatsapp' && accountType === 'PREPAID') {
                    const packageTotalValue = parseCurrency(value);

                    if (packageTotalValue <= 0) {
                      return Promise.reject(
                        new Error('Valor total do pacote deve ser maior que 0.'),
                      );
                    }
                  }

                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input
              style={{ width: '100%', borderRadius: 16 }}
              placeholder="R$ 0,00"
              onChange={e => {
                form.setFieldsValue({
                  package_total_value: sanitizeCurrencyInput(e.target.value),
                });
              }}
              onBlur={e => {
                form.setFieldsValue({
                  package_total_value: formatCurrency(e.target.value),
                });
              }}
            />
          </Form.Item>
        </Col>
        {mediaName === 'whatsapp' && accountType === 'PREPAID' && extraPackagesTotal > 0 && (
          <Col span={6}>
            <Form.Item
              label="Valor pacote extra"
              name="package_extra_total_display"
            >
              <Input
                style={{ width: '100%', borderRadius: 16 }}
                disabled
              />
            </Form.Item>
          </Col>
        )}
      </Row>

      {accountType === 'PREPAID' && (
        <>
          {mediaName === 'whatsapp' && (
            <>
              <Divider />
              <Row align="middle" style={{ marginBottom: 20 }}>
                <Col>
                  <h3 style={{ margin: 0, marginRight: 15 }}>WhatsApp Ativo</h3>
                </Col>
                {licenseDisabled && (
                  <Col style={{ marginRight: 15 }}>
                    <Tag color="error">Licença inativa</Tag>
                  </Col>
                )}
                <Col>
                  <Form.Item
                    name="dividir_categorias"
                    valuePropName="checked"
                    style={{ margin: 0 }}
                  >
                    <Switch />
                  </Form.Item>
                </Col>
                <Col style={{ marginLeft: 8 }}>
                  <span>Dividir valor entre as categorias</span>
                  <Tooltip title="Quando ativado, você pode distribuir o saldo entre as categorias. Quando desativado, todo o saldo ficará disponível para uso livre.">
                    <InfoCircleFilled
                      style={{
                        marginLeft: 4,
                        cursor: 'pointer',
                        color: 'rgba(0, 0, 0, 0.45)',
                      }}
                    />
                  </Tooltip>
                </Col>
              </Row>

              {/* SE ESTIVER ATIVADO: Mostra a divisão por categorias */}
              {dividirCategorias ? (
                <>
                  {/* Marketing */}
                  <Row gutter={16} align="middle" style={{ marginBottom: 12 }}>
                    <Col
                      span={3}
                      style={{ textAlign: 'center', fontSize: '16px' }}
                    >
                      Marketing
                    </Col>
                    <Col span={5}>
                      <Form.Item
                        label="Limite da categoria"
                        name="package_mkt_value"
                        rules={[
                          { required: true, message: 'Campo obrigatório' },
                        ]}
                        style={{ margin: 0 }}
                      >
                        <Input
                          style={{ width: '100%', borderRadius: 16 }}
                          placeholder="R$ 0,00"
                          onChange={e => {
                            form.setFieldsValue({
                              package_mkt_value: sanitizeCurrencyInput(
                                e.target.value,
                              ),
                            });
                          }}
                          onBlur={e => {
                            form.setFieldsValue({
                              package_mkt_value: formatCurrency(e.target.value),
                            });
                          }}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={5}>
                      <Form.Item
                        label="Valor unitário"
                        name="unit_mkt_value"
                        style={{ margin: 0 }}
                        rules={[
                          { required: true, message: 'Campo obrigatório' },
                        ]}
                      >
                        <Input
                          style={{ width: '100%', borderRadius: 16 }}
                          placeholder="R$ 0,00"
                          onChange={e => {
                            form.setFieldsValue({
                              unit_mkt_value: sanitizeCurrencyInput(
                                e.target.value,
                              ),
                            });
                          }}
                          onBlur={e => {
                            form.setFieldsValue({
                              unit_mkt_value: formatCurrency(e.target.value),
                            });
                          }}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={5}>
                      <Form.Item
                        label="Saldo restante"
                        name="remaining_balance_mkt"
                        style={{ margin: 0 }}
                      >
                        <Input
                          style={{
                            width: '100%',
                            borderRadius: 16,
                            color: getSaldoColor(
                              parseCurrency(
                                form.getFieldValue('remaining_balance_mkt'),
                              ),
                              mktLimit,
                            ),
                          }}
                          disabled
                          placeholder="R$ 0,00"
                        />
                      </Form.Item>
                    </Col>
                    <Col span={6} style={{ paddingTop: 0 }}>
                      <Button
                        type="primary"
                        onClick={() => handleOpenTransfer('mkt')}
                        icon={<span style={{ marginRight: 4 }}>⇆</span>}
                      >
                        Transferir saldo
                      </Button>
                    </Col>
                  </Row>

                  {/* Utilidades */}
                  <Row gutter={16} align="middle" style={{ marginBottom: 12 }}>
                    <Col
                      span={3}
                      style={{ textAlign: 'center', fontSize: '16px' }}
                    >
                      Utilidades
                    </Col>
                    <Col span={5}>
                      <Form.Item
                        label="Limite da categoria"
                        name="package_utl_value"
                        style={{ margin: 0 }}
                        rules={[
                          { required: true, message: 'Campo obrigatório' },
                        ]}
                      >
                        <Input
                          style={{ width: '100%', borderRadius: 16 }}
                          placeholder="R$ 0,00"
                          onChange={e => {
                            form.setFieldsValue({
                              package_utl_value: sanitizeCurrencyInput(
                                e.target.value,
                              ),
                            });
                          }}
                          onBlur={e => {
                            form.setFieldsValue({
                              package_utl_value: formatCurrency(e.target.value),
                            });
                          }}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={5}>
                      <Form.Item
                        label="Valor unitário"
                        name="unit_utl_value"
                        style={{ margin: 0 }}
                        rules={[
                          { required: true, message: 'Campo obrigatório' },
                        ]}
                      >
                        <Input
                          style={{ width: '100%', borderRadius: 16 }}
                          placeholder="R$ 0,00"
                          onChange={e => {
                            form.setFieldsValue({
                              unit_utl_value: sanitizeCurrencyInput(
                                e.target.value,
                              ),
                            });
                          }}
                          onBlur={e => {
                            form.setFieldsValue({
                              unit_utl_value: formatCurrency(e.target.value),
                            });
                          }}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={5}>
                      <Form.Item
                        label="Saldo restante"
                        name="remaining_balance_utl"
                        style={{ margin: 0 }}
                      >
                        <Input
                          style={{
                            width: '100%',
                            borderRadius: 16,
                            color: getSaldoColor(
                              parseCurrency(
                                form.getFieldValue('remaining_balance_utl'),
                              ),
                              utlLimit,
                            ),
                          }}
                          disabled
                          placeholder="R$ 0,00"
                        />
                      </Form.Item>
                    </Col>
                    <Col span={6} style={{ paddingTop: 0 }}>
                      <Button
                        type="primary"
                        onClick={() => handleOpenTransfer('utl')}
                        icon={<span style={{ marginRight: 4 }}>⇆</span>}
                      >
                        Transferir saldo
                      </Button>
                    </Col>
                  </Row>

                  {/* Autenticação */}
                  <Row gutter={16} align="middle" style={{ marginBottom: 12 }}>
                    <Col
                      span={3}
                      style={{ textAlign: 'center', fontSize: '16px' }}
                    >
                      Autenticação
                    </Col>
                    <Col span={5}>
                      <Form.Item
                        label="Limite da categoria"
                        name="package_auth_value"
                        style={{ margin: 0 }}
                        rules={[
                          { required: true, message: 'Campo obrigatório' },
                        ]}
                      >
                        <Input
                          style={{ width: '100%', borderRadius: 16 }}
                          placeholder="R$ 0,00"
                          onChange={e => {
                            form.setFieldsValue({
                              package_auth_value: sanitizeCurrencyInput(
                                e.target.value,
                              ),
                            });
                          }}
                          onBlur={e => {
                            form.setFieldsValue({
                              package_auth_value: formatCurrency(
                                e.target.value,
                              ),
                            });
                          }}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={5}>
                      <Form.Item
                        label="Valor unitário"
                        name="unit_auth_value"
                        style={{ margin: 0 }}
                        rules={[
                          { required: true, message: 'Campo obrigatório' },
                        ]}
                      >
                        <Input
                          style={{ width: '100%', borderRadius: 16 }}
                          placeholder="R$ 0,00"
                          onChange={e => {
                            form.setFieldsValue({
                              unit_auth_value: sanitizeCurrencyInput(
                                e.target.value,
                              ),
                            });
                          }}
                          onBlur={e => {
                            form.setFieldsValue({
                              unit_auth_value: formatCurrency(e.target.value),
                            });
                          }}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={5}>
                      <Form.Item
                        label="Saldo restante"
                        name="remaining_balance_auth"
                        style={{ margin: 0 }}
                      >
                        <Input
                          style={{
                            width: '100%',
                            borderRadius: 16,
                            color: getSaldoColor(
                              parseCurrency(
                                form.getFieldValue('remaining_balance_auth'),
                              ),
                              authLimit,
                            ),
                          }}
                          disabled
                          placeholder="R$ 0,00"
                        />
                      </Form.Item>
                    </Col>
                    <Col span={6} style={{ paddingTop: 0 }}>
                      <Button
                        type="primary"
                        onClick={() => handleOpenTransfer('auth')}
                        icon={<span style={{ marginRight: 4 }}>⇆</span>}
                      >
                        Transferir saldo
                      </Button>
                    </Col>
                  </Row>

                  {/* Serviço */}
                  <Row gutter={16} align="middle" style={{ marginBottom: 12 }}>
                    <Col
                      span={3}
                      style={{ textAlign: 'center', fontSize: '16px' }}
                    >
                      Serviço
                    </Col>
                    <Col span={5}>
                      <Form.Item
                        label="Limite da categoria"
                        name="package_svc_value"
                        style={{ margin: 0 }}
                        rules={[
                          { required: true, message: 'Campo obrigatório' },
                        ]}
                      >
                        <Input
                          style={{ width: '100%', borderRadius: 16 }}
                          placeholder="R$ 0,00"
                          onChange={e => {
                            form.setFieldsValue({
                              package_svc_value: sanitizeCurrencyInput(
                                e.target.value,
                              ),
                            });
                          }}
                          onBlur={e => {
                            form.setFieldsValue({
                              package_svc_value: formatCurrency(
                                e.target.value,
                              ),
                            });
                          }}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={5}>
                      <Form.Item
                        label="Valor unitário"
                        name="unit_svc_value"
                        style={{ margin: 0 }}
                        rules={[
                          { required: true, message: 'Campo obrigatório' },
                        ]}
                      >
                        <Input
                          style={{ width: '100%', borderRadius: 16 }}
                          placeholder="R$ 0,00"
                          onChange={e => {
                            form.setFieldsValue({
                              unit_svc_value: sanitizeCurrencyInput(
                                e.target.value,
                              ),
                            });
                          }}
                          onBlur={e => {
                            form.setFieldsValue({
                              unit_svc_value: formatCurrency(e.target.value),
                            });
                          }}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={5}>
                      <Form.Item
                        label="Saldo restante"
                        name="remaining_balance_svc"
                        style={{ margin: 0 }}
                      >
                        <Input
                          style={{
                            width: '100%',
                            borderRadius: 16,
                            color: getSaldoColor(
                              parseCurrency(
                                form.getFieldValue('remaining_balance_svc'),
                              ),
                              svcLimit,
                            ),
                          }}
                          disabled
                          placeholder="R$ 0,00"
                        />
                      </Form.Item>
                    </Col>
                    <Col span={6} style={{ paddingTop: 0 }}>
                      <Button
                        type="primary"
                        onClick={() => handleOpenTransfer('svc')}
                        icon={<span style={{ marginRight: 4 }}>⇆</span>}
                      >
                        Transferir saldo
                      </Button>
                    </Col>
                  </Row>

                  {/* Totais de Rodapé */}
                  <Row gutter={16} style={{ marginTop: 16 }}>
                    <Col span={3} />
                    <Col span={5}>
                      <Form.Item
                        label="Total de limite das categorias"
                        name="total_categories_limit"
                        validateStatus={possuiErroDeLimite ? 'error' : ''}
                        style={{ margin: 0 }}
                      >
                        <Input
                          style={{
                            width: '100%',
                            borderRadius: 16,
                            color: possuiErroDeLimite ? '#ff4d4f' : 'inherit',
                          }}
                          disabled
                        />
                      </Form.Item>

                      {/* Mensagem se ultrapassar o valor */}
                      {excedeuLimite && (
                        <div
                          style={{
                            color: '#ff4d4f',
                            fontSize: '12px',
                            marginTop: '4px',
                            lineHeight: '14px',
                          }}
                        >
                          O valor distribuído excede o total do pacote em R${' '}
                          {valorDiferenca.toFixed(2).replace('.', ',')}. Ajuste
                          os limites das categorias.
                        </div>
                      )}

                      {/* Nova Mensagem se faltar distribuir parte do valor (conforme image_0a0efb.png) */}
                      {ficouAbaixoLimite && (
                        <div
                          style={{
                            color: '#2CA128',
                            fontSize: '12px',
                            marginTop: '4px',
                            lineHeight: '14px',
                          }}
                        >
                          Faltam R${' '}
                          {valorDiferenca.toFixed(2).replace('.', ',')} para
                          distribuir. A soma das categorias deve ser igual ao
                          valor total do pacote.
                        </div>
                      )}
                    </Col>

                    <Col span={5} offset={5}>
                      <Form.Item
                        label="Total de saldo restante"
                        name="total_remaining_balance"
                        style={{ margin: 0 }}
                      >
                        <Input
                          style={{
                            width: '100%',
                            borderRadius: 16,
                            color: possuiErroDeLimite ? '#ff4d4f' : '#52c41a',
                          }}
                          disabled
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </>
              ) : (
                /* SE ESTIVER DESATIVADO: Mostra a linha única de Pacote */
                <Row gutter={16} align="middle">
                  <Col
                    span={3}
                    style={{ textAlign: 'center', fontSize: '16px' }}
                  >
                    Pacote
                  </Col>
                  <Col span={5}>
                    <Form.Item
                      label="Total de limite:"
                      name="package_limit_total_display"
                    >
                      <Input
                        style={{ width: '100%', borderRadius: 16 }}
                        disabled
                      />
                    </Form.Item>
                  </Col>
                  <Col span={5}>
                    <Form.Item
                      label="Valor unitário:"
                      name="unit_value"
                      rules={[{ required: true, message: 'Campo obrigatório' }]}
                    >
                      <Input
                        style={{ width: '100%', borderRadius: 16 }}
                        placeholder="R$ 0,00"
                        onChange={e => {
                          form.setFieldsValue({
                            unit_value: sanitizeCurrencyInput(e.target.value),
                          });
                        }}
                        onBlur={e => {
                          form.setFieldsValue({
                            unit_value: formatCurrency(e.target.value),
                          });
                        }}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={5}>
                    <Form.Item label="Saldo restante:" name="remaining_balance">
                      <Input
                        style={{
                          width: '100%',
                          borderRadius: 16,
                          color: getSaldoColor(
                            parseCurrency(
                              form.getFieldValue('remaining_balance'),
                            ),
                            packageTotalWithExtras,
                          ),
                          WebkitTextFillColor: getSaldoColor(
                            parseCurrency(
                              form.getFieldValue('remaining_balance'),
                            ),
                            packageTotalWithExtras,
                          ),
                        }}
                        disabled
                      />
                    </Form.Item>
                  </Col>
                  <Col span={4}>
                    <Button
                      type="primary"
                      style={{ borderRadius: 16 }}
                      icon={<span>+ </span>}
                      onClick={onClickExtraPackage}
                    >
                      Pacote extra
                    </Button>
                  </Col>
                </Row>
              )}
            </>
          )}

          {mediaName === 'sms' && (
            <Row gutter={16}>
              <Col span={8}>
                <Row>
                  <Col span={24}>
                    <Form.Item
                      label="Quantidade de mensagens:"
                      name="available_quantity"
                    >
                      <InputNumber
                        min={0}
                        precision={0}
                        style={{ width: '100%', borderRadius: 16 }}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Row>
                  <Col span={24}>
                    <Form.Item label="Valor do Pacote:" name="package_value">
                      <Input
                        style={{ width: '100%', borderRadius: 16 }}
                        placeholder="R$ 0,00"
                        onChange={e => {
                          form.setFieldsValue({
                            package_value: sanitizeCurrencyInput(e.target.value),
                          });
                        }}
                        onBlur={e => {
                          form.setFieldsValue({
                            package_value: formatCurrency(e.target.value),
                          });
                        }}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Row>
                  <Col span={24}>
                    <Form.Item
                      label="Valor da Mensagem Extra:"
                      name="extra_message_value"
                    >
                      <Input
                        style={{ width: '100%', borderRadius: 16 }}
                        placeholder="R$ 0,00"
                        onChange={e => {
                          form.setFieldsValue({
                            extra_message_value: sanitizeCurrencyInput(
                              e.target.value,
                            ),
                          });
                        }}
                        onBlur={e => {
                          form.setFieldsValue({
                            extra_message_value: formatCurrency(e.target.value),
                          });
                        }}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Col>
            </Row>
          )}
        </>
      )}

      <Divider />
      <Row gutter={8}>
        <Col>
          <Button
            type="primary"
            shape="round"
            htmlType="submit"
            loading={loading}
            disabled={possuiErroDeLimite}
            icon={<SaveFilled />}
          >
            {formLicenseId ? 'Atualizar licença' : 'Salvar'}
          </Button>
        </Col>
        <Col>
          <Button shape="round" onClick={cancelLicenseEdit}>
            Cancelar
          </Button>
        </Col>
      </Row>
    </Form>
  );
};

export default LicenseForm;